import { useEffect, useRef, useState } from "react";

// Fix AI (2026-10-23): "compacto" = celular, vertical u horizontal. Conversaciones
// muestra una sola columna a la vez (la lista o el chat) en vez de las dos juntas.
const COMPACT_QUERY = "(max-width: 767px), (max-width: 1023px) and (max-height: 499px)";

function matchesCompact() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(COMPACT_QUERY).matches;
}

export function useCompactLayout() {
  const [compact, setCompact] = useState(matchesCompact);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(COMPACT_QUERY);
    const onChange = () => setCompact(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return compact;
}

// Parte visible de la pantalla (arriba y alto). En el iPhone cambia al abrir el
// teclado; asi el chat a pantalla completa deja la caja de texto sobre el teclado.
export function useVisibleViewport(enabled) {
  const [box, setBox] = useState(null);
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!enabled || !vv) {
      setBox(null);
      return undefined;
    }
    const update = () => setBox({ top: Math.round(vv.offsetTop), height: Math.round(vv.height) });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [enabled]);
  return box;
}

// Fix AI (2026-10-23, ajuste de la aplicacion): entrada de historial del chat abierto a pantalla
// completa en el celular. Hay UNA sola entrada propia (marcada en history.state) y un unico dueno
// (este modulo), asi que no se duplica ni se toca el historial ajeno:
// - "atras" del celular: consume la entrada y cierra el chat sin salir de la pantalla;
// - cerrar el chat con la flecha, pasar a escritorio o desmontar la pantalla: la entrada se saca solo
//   si sigue siendo la actual (si hay algo de otros encima, no se toca);
// - volver a compacto con el chat abierto, StrictMode o un remount reutilizan la misma entrada.
const ENTRY_KEY = "convChatEntry";
const REMOVAL_MAX_MS = 3000;
const entry = { token: null, removing: false, wanted: 0, timer: null, onBack: null, listening: false };

function entryIsCurrent() {
  return Boolean(entry.token) && window.history.state?.[ENTRY_KEY] === entry.token;
}

function openEntry() {
  const marked = window.history.state?.[ENTRY_KEY];
  if (marked) {
    // ya hay una entrada nuestra puesta (StrictMode, escritorio->compacto, o una que quedo de antes): se reutiliza
    entry.token = marked;
    return;
  }
  entry.token = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  window.history.pushState({ ...(window.history.state || {}), [ENTRY_KEY]: entry.token }, "");
}

function removalDone() {
  clearTimeout(entry.timer);
  entry.removing = false;
  if (entry.wanted > 0) openEntry(); // se volvio a abrir un chat mientras la entrada todavia salia
}

function closeEntry() {
  if (entry.wanted > 0 || !entry.token) return;
  const mine = entryIsCurrent();
  entry.token = null;
  if (!mine) return; // quedo algo de otros encima, o la entrada ya no esta: no se toca el historial ajeno
  entry.removing = true;
  entry.timer = setTimeout(removalDone, REMOVAL_MAX_MS);
  window.history.back();
}

function onPopState() {
  if (entry.removing) {
    removalDone(); // fin de la salida que pidio este modulo (no es el "atras" del usuario)
    return;
  }
  if (entry.token && window.history.state?.[ENTRY_KEY] !== entry.token) {
    entry.token = null; // el usuario salio de la entrada con "atras": se cierra el chat
    if (entry.onBack) entry.onBack();
  }
}

export function useChatHistoryEntry(active, onBack) {
  const backRef = useRef(onBack);
  backRef.current = onBack;
  useEffect(() => {
    if (!active) return undefined;
    entry.wanted += 1;
    entry.onBack = () => backRef.current();
    if (!entry.listening) {
      window.addEventListener("popstate", onPopState);
      entry.listening = true;
    }
    if (!entry.removing) openEntry();
    return () => {
      entry.wanted -= 1;
      // diferido: en StrictMode el cleanup y el setup siguiente van juntos y no hay que sacar la entrada
      Promise.resolve().then(closeEntry);
    };
  }, [active]);
}
